import {ImageSearchRequest, ImageSearchRequestCreation} from '@/domain/entities/ImageSearchRequest';
import imageSearchRequestRepository from '@/domain/repository/imageSearchRequestRepository';
import {withErrorHandling} from '@/domain/useCases/exceptionHandling';
import dayjs, {Dayjs} from 'dayjs';
import {Polygon} from 'geojson';

export type DateRange = {
  start: Dayjs,
  end: Dayjs,
}

// The API rejects end dates in the future and compares them against its own clock. A range ending today
// is sent as "now" minus this margin, so that a browser clock running slightly ahead of the server's
// does not make the request invalid. Archived imagery takes hours to be published, so nothing is lost.
const CLOCK_SKEW_MARGIN_MINUTES = 5;

// End of the selected last day (in the user's local time zone), capped at "now minus margin".
// It is an absolute instant: serialized with toISOString() it is sent in UTC.
const resolveEndDate = (selectedEnd: Dayjs): Date => {
  const endOfDay = selectedEnd.endOf('day');
  const latestAllowed = dayjs().subtract(CLOCK_SKEW_MARGIN_MINUTES, 'minute');
  return (endOfDay.isAfter(latestAllowed) ? latestAllowed : endOfDay).toDate();
};

const createImageSearchRequest = async (
  dateRange: Partial<DateRange>, areaOfInterest?: Polygon
): Promise<ImageSearchRequest | undefined> => {
  if (!dateRange.start || !dateRange.end || !areaOfInterest) return;

  const request: ImageSearchRequestCreation = {
    areaOfInterest: {
      type: 'MultiPolygon',
      coordinates: [areaOfInterest.coordinates]
    },
    startDate: dateRange.start.toDate(),
    endDate: resolveEndDate(dateRange.end)
  };

  return withErrorHandling(
    () => imageSearchRequestRepository.create(request),
    {genericErrorKey: 'request.error'}
  );
};

export default createImageSearchRequest;
